import * as React from "react"

const MOBILE_BREAKPOINT = 768

export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }
    mql.addEventListener("change", onChange)
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    return () => mql.removeEventListener("change", onChange)
  }, [])

  return !!isMobile
}

// 手机竖屏判定：窄屏(宽<768)且纵向(orientation:portrait)。用于「请横屏」提示。
// 平板竖屏(宽≥768)不命中,不打扰。仅用于登录后的仪表盘提示浮层,不涉及输入框弹键盘,
// 故可继续用视口媒体查询(键盘态下短暂判为非竖屏只会让浮层暂隐,无害)。
export function useIsMobilePortrait() {
  const query = `(max-width: ${MOBILE_BREAKPOINT - 1}px) and (orientation: portrait)`
  const [is, setIs] = React.useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  )
  React.useEffect(() => {
    const mql = window.matchMedia(query)
    const onChange = () => setIs(mql.matches)
    mql.addEventListener("change", onChange)
    setIs(mql.matches)
    return () => mql.removeEventListener("change", onChange)
  }, [query])
  return is
}

// 「手机横屏舞台」判定：窄屏(宽<1024,即桌面 lg 断点以下)且设备**物理横屏**。
// 用于 login/auth 页在「竖屏流式表单 ↔ 844×390 横屏舞台」间切换。
//
// ⚠️ 为何不用 CSS (orientation:landscape)/(max-height:500px) 媒体查询,而必须用 screen.orientation：
//   真机(尤其微信 X5 webview / 部分安卓浏览器)竖屏聚焦输入框弹起软键盘时,会压缩**布局视口**高度,
//   在窄机上足以让视口「宽>高」——于是 CSS 的 (orientation:landscape) 与 (max-height:500px) 双双误判为
//   横屏 → 竖屏表单被 display:none → 输入框卸载失焦 → 软键盘弹不起(用户实测现象)。
//   screen.orientation / window.orientation 反映**设备物理朝向**,软键盘不会旋转设备,故不受影响,是唯一可靠信号。
// 宽度用 innerWidth<1024:软键盘只压高度不改宽度,横竖屏下手机宽度均 <1024,可稳定区分「桌面 vs 手机」。
export function useIsMobileLandscapeStage() {
  const compute = React.useCallback(() => {
    if (typeof window === "undefined") return false
    const narrow = window.innerWidth < 1024
    if (!narrow) return false // 桌面/大平板宽度,始终走桌面画布,与旧 lg 断点一致
    const so = window.screen && window.screen.orientation
    let landscape: boolean
    if (so && typeof so.type === "string") {
      landscape = so.type.startsWith("landscape") // 现代浏览器/安卓 X5(Chromium≥38)
    } else if (typeof (window as unknown as { orientation?: number }).orientation === "number") {
      landscape = Math.abs((window as unknown as { orientation: number }).orientation) === 90 // 旧版 iOS WKWebView
    } else {
      landscape = window.innerWidth > window.innerHeight // 兜底(极老 webview,无物理朝向 API)
    }
    return landscape
  }, [])

  const [is, setIs] = React.useState(compute)
  React.useEffect(() => {
    const onChange = () => setIs(compute())
    window.addEventListener("resize", onChange)
    window.addEventListener("orientationchange", onChange)
    const so = window.screen && window.screen.orientation
    so?.addEventListener?.("change", onChange)
    onChange()
    return () => {
      window.removeEventListener("resize", onChange)
      window.removeEventListener("orientationchange", onChange)
      so?.removeEventListener?.("change", onChange)
    }
  }, [compute])
  return is
}
